import React, { useState, useEffect } from 'react';
import { BRU_GAPS_AUDITS_SOLUTIONS_COLUMNS } from 'constants/reports';
import { GapFilterData } from 'store/ducks/Reporting/types';
import { useFilterOptions } from 'utils/hooks/useFilterOptions';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import Helpers from 'utils/Helpers';
import ApiService from 'utils/ApiCalls';
import CustomTable from 'components/Tables/CustomTable';
import * as Styled from './BruGapsAuditsSolutions.styled';

const BruGapsAuditsSolutions: React.FC = () => {
  const { loading } = useFilterOptions();
  const [data, setData] = useState<GapFilterData[]>([]);

  const { orgContext, lang } = useSelector((state: RootState) => ({
    orgContext: state.global.hierarchicalLevelsScope,
    lang: state.global.lang
  }));

  const filterParams = Helpers.toApiFilters(orgContext);
  const normalizedFilters = Helpers.normalizeFilters(
    Helpers.toApiFilters(orgContext)
  );

  const handleDisplay = async () => {
    try {
      if (!orgContext || Object.keys(orgContext).length === 0) return;
      const hasFilters = Object.keys(filterParams).length > 0;
      if (!hasFilters) return;

      const response = await ApiService.getBruGapsAuditsSolutions(
        lang,
        normalizedFilters
      );
      const responseData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      const formattedData = responseData.flatMap((item: any) => {
        const gaps = Object.values(item.GAPS || {});

        const solutions = Object.values(item.SOLUTIONS || {});

        return gaps.map((gap: any) => ({
          key: gap.gap_id,

          // GAP fields
          gap_id: gap.gap_id,
          gap_description: gap.gap_description,
          gap_due_date: gap.gap_due_date,
          gap_closed: gap.gap_closed,
          gap_created_by: gap.gap_created_by,
          gap_created_on: gap.gap_created_on,

          priority_name: gap.gap_priority_name,
          source_name: gap.gap_source_name,
          owner_name: gap.gap_owner_name,

          // SOLUTIONS (ALL)
          sol_approved: solutions.map((s: any) => s.sol_approved),

          sol_approver_name: solutions.map((s: any) => s.sol_approver_name),

          sol_approver_notified: solutions.map(
            (s: any) => s.sol_approver_notified
          ),

          sol_description: solutions.map((s: any) => s.sol_description),

          sol_evd_required: solutions.map((s: any) => s.sol_evd_required),

          sol_reviewed: solutions.map((s: any) => s.sol_reviewed),

          sol_reviewer_name: solutions.map((s: any) => s.sol_reviewer_name),

          sol_reviewer_notified: solutions.map(
            (s: any) => s.sol_reviewer_notified
          ),

          sol_type_name: solutions.map((s: any) => s.sol_type_name),
          // BRU metadata
          grp_name: item.grp_name,
          ind_name: item.ind_name,
          mod_name: item.mod_name,
          cat_name: item.cat_name,
          bru_key_value: item.bru_key_value,

          cntry_name: item.cntry_name,
          reg_name: item.reg_name,
          ent_name: item.ent_name,
          pfn_name: item.pfn_name
        }));
      });
      setData(formattedData);
    } catch (error) {
      console.error('Failed to fetch BRU Gaps Audits Solutions data:', error);
    }
  };

  useEffect(() => {
    if (!lang) return;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    handleDisplay();
  }, [lang, orgContext]);

  return (
    <Styled.VerticalSpace direction='vertical' size='large'>
      <CustomTable
        drawTable={true}
        itemsCount={data.length}
        columns={BRU_GAPS_AUDITS_SOLUTIONS_COLUMNS}
        dataSource={data}
        scroll={{ x: 1600, y: 400 }}
        loading={loading}
        rowKey={(record: GapFilterData) => record.key}
        sticky
      />
    </Styled.VerticalSpace>
  );
};

export default BruGapsAuditsSolutions;
